import React from "react";
import { IconChevronDown, IconChevronUp, IconSquareRoundedPlus } from "@tabler/icons-react";
import HistoryIcon from "assets/HistoryIcon";
import { RefreshIcon } from "assets/RefreshIcon";
import MenuClose from "assets/MenuClose";
import MenuOpen from "assets/MenuOpen";
import { Conversation } from "./types";
import { AgenticConversationSidebarContainer, RefreshingAnimation, SidebarIcon } from "../../ac2Alerts/alerts.styled";

interface ConversationSidebarProps {
  apptheme: any;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  handleLabelClick: (label: string) => void;
  isRefreshing: boolean;
  setShowhistory: (show: boolean) => void;
  hidden: { [id: string]: boolean };
  todayConversations: Conversation[];
  yesterdayConversations: Conversation[];
  pastWeekConversations: Conversation[];
  lastMonthConversations: Conversation[];
  olderConversations: Conversation[];
  selectedConversation: string | null;
  handleSelectConversation: (conversation: Conversation) => void;
  fullScreen: boolean;
}

// Extracted conversation item component to prevent re-renders
const ConversationItem = React.memo(({ 
  conversation, 
  apptheme, 
  isSelected, 
  onSelect 
}: { 
  conversation: Conversation;
  apptheme: any;
  isSelected: boolean;
  onSelect: (conversation: Conversation) => void;
}) => (
  <div
    key={conversation.id}
    className={`p-2 item-start text-sm rounded cursor-pointer mb-[0.25rem] ${
      apptheme.name === "dark-theme"
        ? "hover:bg-gray-700 text-white"
        : "hover:bg-purple-100 text-black"
    } ${
      isSelected
        ? apptheme.name === "dark-theme"
          ? "bg-gray-700 text-white"
          : "bg-purple-100 text-black"
        : ""
    }`}
    onClick={() => onSelect(conversation)}
  >
    {conversation.title}
  </div>
));

// Extracted section header component
const SectionHeader = React.memo(({ 
  label, 
  isHidden, 
  onClick 
}: { 
  label: string;
  isHidden: boolean;
  onClick: () => void;
}) => (
  <div
    className="flex items-center justify-between cursor-pointer"
    onClick={onClick}
  >
    <span className="text-base">{label}</span>
    <SidebarIcon>
      {isHidden ? 
        <IconChevronDown className="text-gray-500" /> : 
        <IconChevronUp className="text-gray-500" />
      }
    </SidebarIcon>
  </div>
));

// This component is no longer needed since we're using the SidebarIcon component
// const IconWrapper = ({ children }) => (
//   <div className=" flex items-center justify-center">
//     {children}
//   </div>
// );

// Main sidebar component
const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  apptheme,
  sidebarCollapsed,
  setSidebarCollapsed,
  handleLabelClick,
  isRefreshing,
  setShowhistory,
  hidden,
  todayConversations,
  yesterdayConversations,
  pastWeekConversations,
  lastMonthConversations,
  olderConversations,
  selectedConversation,
  handleSelectConversation,
  fullScreen
}) => {
  return (
    <AgenticConversationSidebarContainer
      className={`h-full transition-all py-3 duration-500 ease-in-out ${
        apptheme.name === "dark-theme" ? "bg-[#212121] text-white" : "bg-[#ffffff] text-black"
      } ${!sidebarCollapsed ? "w-[240px]" : "w-[60px]"}`}
    >
      <div className="h-full flex flex-col">
        <div className="p-3 flex flex-col items-start">
          <button 
            className={`mb-5 cursor-pointer flex ${sidebarCollapsed ? "justify-center w-full" : "justify-start"}`}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            {sidebarCollapsed ? (
              <SidebarIcon>
                <MenuClose />
              </SidebarIcon>
            ) : (
              <div className="flex items-center">
                <SidebarIcon className=" mr-3">
                  <MenuOpen />
                </SidebarIcon>
                <span className="text-sm font-medium">Conversation</span>
              </div>
            )}
          </button>
          
          <button 
            className={`flex items-center cursor-pointer py-2 mb-2 ${
              sidebarCollapsed ? "justify-center w-full" : "justify-start"
            }`}
            onClick={() => handleLabelClick("refresh")}
          >
            {isRefreshing ? (
              <RefreshingAnimation>
                <RefreshIcon />
              </RefreshingAnimation>
            ) : (
              <SidebarIcon>
                <RefreshIcon />
              </SidebarIcon>
            )}
            {!sidebarCollapsed && 
              <span className="text-sm font-medium ml-3 whitespace-nowrap overflow-hidden transition-all">
                Refresh Conversations
              </span>
            }
          </button>
          
          <button 
            className={`flex items-center cursor-pointer py-2 mb-2 ${
              sidebarCollapsed ? "justify-center w-full" : "justify-start"
            }`}
            onClick={() => handleLabelClick("new")}
          >
            <SidebarIcon>
              <IconSquareRoundedPlus />
            </SidebarIcon>
            {!sidebarCollapsed && 
              <span className="text-sm font-medium ml-3 whitespace-nowrap overflow-hidden transition-all">
                New Conversation
              </span>
            }
          </button>
          
          <button 
            className={`flex items-center cursor-pointer py-2 mb-2 ${
              sidebarCollapsed ? "justify-center w-full" : "justify-start"
            }`}
            onClick={() => setShowhistory(true)}
          >
            <SidebarIcon>
              <HistoryIcon />
            </SidebarIcon>
            {!sidebarCollapsed && 
              <span className="text-sm font-medium ml-3 whitespace-nowrap overflow-hidden transition-all">
                Full History
              </span>
            }
          </button>
        </div>

        {!sidebarCollapsed && (
          <div
            className="px-3 overflow-y-auto flex-1"
            style={{
              minHeight: 0,
            }}
          >
            <div className="flex flex-col space-y-4 pt-2">
              {/* Today section */}
              <div className="flex flex-col">
                <SectionHeader 
                  label="Today" 
                  isHidden={hidden.today}
                  onClick={() => handleLabelClick("today")}
                />
                
                {!hidden.today && todayConversations.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {todayConversations.map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        apptheme={apptheme}
                        isSelected={selectedConversation === conversation.id}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </div>
                )}
                
                {!hidden.today && todayConversations.length === 0 && (
                  <div className="ml-2 mt-1 p-2 text-sm text-gray-500">
                    No conversations today
                  </div>
                )}
              </div>

              {/* Yesterday section */}
              <div>
                <SectionHeader 
                  label="Yesterday" 
                  isHidden={hidden.yesterday}
                  onClick={() => handleLabelClick("yesterday")}
                />
                
                {!hidden.yesterday && yesterdayConversations.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {yesterdayConversations.map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        apptheme={apptheme}
                        isSelected={selectedConversation === conversation.id}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </div>
                )}
                
                {!hidden.yesterday && yesterdayConversations.length === 0 && (
                  <div className="mt-1 p-2 text-sm text-gray-500">
                    No conversations yesterday
                  </div>
                )}
              </div>

              {/* Past Week section */}
              <div>
                <SectionHeader 
                  label="Past Week" 
                  isHidden={hidden.pastWeek}
                  onClick={() => handleLabelClick("pastWeek")}
                />
                
                {!hidden.pastWeek && pastWeekConversations.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1">
                    {pastWeekConversations.map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        apptheme={apptheme}
                        isSelected={selectedConversation === conversation.id}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </div>
                )}
                
                {!hidden.pastWeek && pastWeekConversations.length === 0 && (
                  <div className="mt-1 p-2 text-sm text-gray-500">
                    No conversations from past week
                  </div>
                )}
              </div>

              {/* Last Month section */}
              <div>
                <SectionHeader 
                  label="Last Month" 
                  isHidden={hidden.lastMonth}
                  onClick={() => handleLabelClick("lastMonth")}
                />
                
                {!hidden.lastMonth && lastMonthConversations.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1">
                    {lastMonthConversations.map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        apptheme={apptheme}
                        isSelected={selectedConversation === conversation.id}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </div>
                )}
                
                {!hidden.lastMonth && lastMonthConversations.length === 0 && (
                  <div className="mt-1 p-2 text-sm text-gray-500">
                    No conversations from last month
                  </div>
                )}
              </div>

              {/* Older section */}
              <div>
                <SectionHeader 
                  label="Older" 
                  isHidden={hidden.older}
                  onClick={() => handleLabelClick("older")}
                />
                
                {!hidden.older && olderConversations.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-48 overflow-y-auto pr-1">
                    {olderConversations.map((conversation) => (
                      <ConversationItem
                        key={conversation.id}
                        conversation={conversation}
                        apptheme={apptheme}
                        isSelected={selectedConversation === conversation.id}
                        onSelect={handleSelectConversation}
                      />
                    ))}
                  </div>
                )}
                
                {!hidden.older && olderConversations.length === 0 && (
                  <div className="mt-1 p-2 text-sm text-gray-500">
                    No older conversations
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AgenticConversationSidebarContainer>
  );
};

export default React.memo(ConversationSidebar);